import APITemplate from '@api/APITemplate'
import store from '@state/store'
import con from '@services/connection'
import {
  boasVindas as URL_BOAS_VINDAS,
  boasVindasClipe as URL_CLIPE,
} from '@modules/aawz-intelligence-v2/constants/URL'

const ERRO_LEITURA =
  'Não foi possível verificar a mensagem de boas-vindas agora. Tente novamente mais tarde ou contate o suporte.'
const ERRO_MARCACAO =
  'Não foi possível registrar a entrega da mensagem de boas-vindas. Ela pode aparecer de novo na próxima sessão.'
// Este NUNCA chega à tela: o clipe é enfeite, e a sua ausência já é um estado
// previsto (o player mostra o retrato ou o selo). A mensagem existe para o
// diagnóstico de quem for ler o envelope.
const ERRO_CLIPE = 'Não foi possível buscar o clipe do avatar agora.'
// ⚠️ A URL do clipe é SHORT-LIVED por contrato do vendor — medida contra o servidor real
// em 18/09/2026: `expira_em` a **5 minutos** da resposta. Um clipe cacheado com a URL
// vencida não é um clipe: é um 403 esperando para acontecer no meio do primeiro contato.
// A margem existe porque entre servir do cache e o `<video>` buscar o arquivo passa tempo
// — uma URL que vence em dois segundos não serve para um vídeo que vai começar agora.
const MARGEM_DE_VALIDADE_MS = 30000

const STATUS_OK = 200
// Os dois únicos canais do contrato. O front não inventa um terceiro.
export const CANAL_AUDIO = 'audio'
export const CANAL_TEXTO = 'texto'
const CANAIS = [CANAL_AUDIO, CANAL_TEXTO]

// A boas-vindas do primeiro contato (spec-009 [US-2]) — a leitura que diz se ela deve
// acontecer e a marcação de que aconteceu. Molde de `apiAiFrameworkTermos`: o método faz o
// request E persiste por `store.dispatch` dos setters de `store/boas-vindas.js`.
// Envelope { status, message, value } de `response.data` — o caller lê o `status` do BODY,
// nunca o HTTP (contrato AAWZ: 200 sempre).
//
// ★ O FRONT NÃO DECIDE SE É O PRIMEIRO CONTATO. Ele pergunta. A régua (não ter marca E não
// ter conversa anterior) é do servidor porque a checagem não é atômica: quem impede a
// entrega dupla é a unique do banco. Um `if` no cliente daria a ilusão da mesma garantia
// com duas abas abertas.
class APIBoasVindas extends APITemplate {
  constructor() {
    super()

    // O namespace com que `index.js` registra a store (`registerModule`) — o mesmo de
    // `apiFolders`/`apiSuggestions`. Com hífens: é a chave, não um identificador JS.
    this.MODULE = 'aawz-intelligence-v2'

    // STORE KEYS
    this.KEY_ENTREGA_LOADING = `${this.MODULE}/boasVindasEntregaLoading`

    // STORE ACTIONS
    Object.assign(this, {
      ACTION_SET: `${this.MODULE}/setBoasVindas`,
      ACTION_SET_LOADING: `${this.MODULE}/setBoasVindasLoading`,
      ACTION_SET_ERRO: `${this.MODULE}/setBoasVindasErro`,
      ACTION_SET_ENTREGA_LOADING: `${this.MODULE}/setBoasVindasEntregaLoading`,
      ACTION_SET_ENTREGA_ERRO: `${this.MODULE}/setBoasVindasEntregaErro`,
    })

    // Número de ordem do último disparo da leitura (versionamento). Estado de REQUISIÇÃO,
    // e por isso na instância.
    this.seq = 0

    // ── O CLIPE ──
    // ★ Estado de REQUISIÇÃO na instância, nunca em escopo de módulo: dois
    // consumidores simultâneos compartilhando a mesma variável é race garantida.
    // `_clipeEmVoo` junta a rajada num request só; `_clipePronto` guarda o
    // desfecho FINAL (e só ele — ver `carregarClipe`).
    this._clipeEmVoo = null
    this._clipePronto = null
  }

  /**
   * GET — o servidor diz se a boas-vindas deve ser entregue a ESTE profissional.
   *
   * ★ VERSIONAMENTO, e não guard: todo pedido sai e quem é descartada é a resposta
   * obsoleta, de sucesso ou de falha.
   *
   * Leitura que falha deixa `boasVindas` em `null`, e `null` NÃO é "deve entregar": quem
   * não sabe não entrega. Mostrar a mensagem de primeiro contato por omissão é
   * exatamente o defeito que a US-2 quer evitar — e o custo de não mostrar é zero, porque
   * a próxima sessão pergunta de novo.
   */
  async carregar() {
    const seq = ++this.seq
    store.dispatch(this.ACTION_SET_LOADING, true)

    try {
      const r = this.comoEnvelope(await con.get(URL_BOAS_VINDAS), ERRO_LEITURA)
      if (seq !== this.seq) return r
      this.aplicarLeitura(r)
      return r
    } finally {
      if (seq === this.seq) store.dispatch(this.ACTION_SET_LOADING, false)
    }
  }

  /**
   * POST — marca que a boas-vindas FOI entregue, e por qual canal.
   *
   * ★ Canal inválido é recusado ANTES da URL, sem request: `audio` e `texto` são os dois
   * valores do contrato, e mandar um terceiro sujaria o registro que existe justamente
   * para medir como a mensagem chegou.
   *
   * ★ Guard BLOQUEANTE: o segundo pedido não sai. O servidor é idempotente
   * (`ja_entregue: true` é sucesso, não erro — quem já estava marcado continua marcado),
   * mas duas idas à rede para marcar a mesma entrega são desperdício.
   *
   * ★ A FALHA AQUI NÃO DESFAZ A EXPERIÊNCIA. A mensagem já está na tela quando esta
   * chamada acontece; se a marcação não gravar, o pior desfecho é a pessoa ver a
   * boas-vindas de novo numa próxima sessão. Por isso o erro fica só na store (para
   * diagnóstico) e NÃO vira snack: um aviso de falha sobre algo que ela não pediu e não
   * pode resolver só quebraria o primeiro contato que acabou de dar certo.
   */
  marcarEntregue({ canal = null } = {}) {
    const recusa = this.recusaSemCanal(canal)
    if (recusa) return Promise.resolve(recusa)
    if (this.entregaEmCurso()) return Promise.resolve(undefined)

    store.dispatch(this.ACTION_SET_ENTREGA_LOADING, true)
    return this.enviarMarcacao(canal).finally(() =>
      store.dispatch(this.ACTION_SET_ENTREGA_LOADING, false)
    )
  }

  /**
   * GET — o clipe de fundo do avatar, já gerado pelo backend.
   *
   * ★ **UM request por rajada, e o pronto fica em cache.** A mesma tela tem
   * DOIS consumidores do mesmo rosto (o player da boas-vindas e o avatar da
   * conversa), e cada montagem deles pede o clipe. Sem a junção, uma passagem
   * pela tela vira dois GETs numa rota que, no servidor, dispara GERAÇÃO — e a
   * geração é paga em créditos do vendor. Um rosto só, uma busca só.
   *
   * ⚠️ **Só o desfecho PRONTO entra no cache.** `queued`/`processing` é
   * resposta em trânsito: cacheá-la deixaria a tela sem vídeo pelo resto da
   * sessão mesmo depois de o clipe ficar pronto — o defeito silencioso que a
   * pessoa nunca reportaria porque nunca soube que havia vídeo.
   *
   * ★ NUNCA rejeita e NUNCA lança: quem consome é
   * `criarProvedorDeClipe({ buscar })`, cuja degradação é o contrato. Vendor
   * fora do ar, chave ausente no servidor e clipe inexistente caem todos no
   * mesmo lugar — a tela segue com o retrato ou com o selo.
   */
  carregarClipe() {
    if (this._clipePronto && !this.clipeExpirou(this._clipePronto)) {
      return Promise.resolve(this._clipePronto)
    }
    // Vencido é o mesmo que não ter: some do cache e vira busca nova, que o
    // servidor atende reassinando a URL do clipe que já existe (sem gerar de novo).
    this._clipePronto = null
    if (this._clipeEmVoo) return this._clipeEmVoo
    this._clipeEmVoo = this.buscarClipe()
    return this._clipeEmVoo
  }

  // ---------- HELPERS ----------

  async buscarClipe() {
    try {
      const r = this.comoEnvelope(await con.get(URL_CLIPE), ERRO_CLIPE)
      if (this.clipeEstaPronto(r)) this._clipePronto = r
      return r
    } finally {
      // No `finally`: a próxima montagem tem de poder tentar de novo, inclusive
      // depois de uma falha — é assim que um `processing` vira vídeo sem F5.
      this._clipeEmVoo = null
    }
  }

  /** A URL guardada já venceu (ou vence dentro da margem)? */
  clipeExpirou(r, agora = Date.now()) {
    const expiraEm = r && r.value ? r.value.expira_em : null
    if (!expiraEm) return false
    const prazo = Date.parse(expiraEm)
    // Data ilegível não é vencimento: seria descartar um clipe bom por causa de
    // um formato que o servidor pode mudar sem avisar.
    if (isNaN(prazo)) return false
    return prazo - MARGEM_DE_VALIDADE_MS <= agora
  }

  /** Pronto é `ready` COM url. O resto é trânsito, e trânsito não se cacheia. */
  clipeEstaPronto(r) {
    if (!r || r.status !== STATUS_OK || !r.value) return false
    return String(r.value.status || '').toLowerCase() === 'ready' && Boolean(r.value.url)
  }

  async enviarMarcacao(canal) {
    // `{ json: true }`: o reqparse do recurso lê `location='json'` — sem o opt-in o
    // `connection.js` manda FormData e o backend responde "Missing required parameter".
    const r = this.comoEnvelope(
      await con.post(URL_BOAS_VINDAS, { canal }, false, { json: true }),
      ERRO_MARCACAO
    )

    const mensagem = r.status === STATUS_OK ? null : r.message || ERRO_MARCACAO
    store.dispatch(this.ACTION_SET_ENTREGA_ERRO, mensagem)
    return r
  }

  /**
   * O que sai daqui é SEMPRE um envelope `{ status, message, value }`.
   *
   * ★ Não basta tratar a resposta nula. A rota pode EXISTIR e devolver algo que não é
   * envelope — o caso real é o servidor respondendo o `index.html` do SPA no lugar da API
   * (rota não registrada com fallback de SPA). Sem esta normalização o chamador receberia
   * uma STRING e leria `r.status` como `undefined`: a comparação `=== 200` daria falso por
   * acidente, e o dia em que alguém escrevesse `r.status !== 200` para decidir OUTRA coisa
   * o acidente mudaria de lado. Erro de rota (404) já chega envelopado pelo
   * `connection.js::tratarErroDeRequest` — este guard é para o resto.
   */
  comoEnvelope(r, mensagemPadrao) {
    if (r && typeof r === 'object' && typeof r.status === 'number') return r
    return this.buildErrorResponse({ message: mensagemPadrao, value: null })
  }

  recusaSemCanal(canal) {
    if (CANAIS.indexOf(canal) !== -1) return null
    // Cita os valores aceitos: mensagem que só diz "inválido" obriga quem lê o log a
    // adivinhar o que era esperado.
    return this.buildErrorResponse({
      message: `canal inválido: informe '${CANAL_AUDIO}' ou '${CANAL_TEXTO}'.`,
      value: null,
      status: 400,
    })
  }

  entregaEmCurso() {
    return Boolean(store.getters[this.KEY_ENTREGA_LOADING])
  }

  aplicarLeitura(r) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET, r.value || null)
      store.dispatch(this.ACTION_SET_ERRO, null)
      return
    }
    store.dispatch(this.ACTION_SET, null)
    store.dispatch(this.ACTION_SET_ERRO, r.message || ERRO_LEITURA)
  }
}

const apiBoasVindas = new APIBoasVindas()

export { apiBoasVindas }
